import clsx from "clsx";
import type { ReactNode, RefObject } from "react";
import { basisLabel, fromNet, rateFor, toNet, toNumber, toStored } from "../../shared/money";
import { ChoiceField, DraftFields, SelectField, TextField } from "../../web/components/fields";
import { useMoney } from "../../web/lib/money";
import { MONEY_PATTERN, type PriceListData, type PriceListSettings, type PriceMode, type PricingRule, type TaxQuote } from "./meta";
import { type Basis, baseForMarkup, markupForTotal, markupOf, markupOfBase, shownPercent, tierFor, underMinimum } from "./pricing";
import { QuotedAmountField, quoteKey, quoteOptions } from "./QuotedAmount";

/*
 * Editing how prices are worked out: one editor for an item's own pricing (its Pricing tab) and a
 * group's (its window), so they look, feel and work the same. Both edit a draft, saved by
 * the bar their window puts underneath (`footer`): its fields update it as they're typed in (TextField
 * `live`), so the bar shows at once, under them, without moving the one being typed in. The layout:
 *
 *   Price is   [ Manual | Markup ]   (a product's: | Labour time)
 *   [ Markup ]   [ Total price ]   (a group priced on each item's cost: [ Tax ], as its items' prices differ)
 *
 * As ServiceM8's pricing strategies: Markup and Total price can both be typed. A fixed price keeps its price (its markup shown is
 * worked out from it, and typing the markup works the price out); a markup keeps its % (typed, or a pricing group's: typing a
 * total works a % of its own out).
 *
 * How it's taxed is picked inside each money box (one setting: changing any changes them all, the
 * amounts kept as typed). Amounts are stored before tax and shown in the draft's tax terms.
 */

export const amountCheck = (label: string) => (v: string) => (MONEY_PATTERN.test(v) && Number(v) >= 0 ? null : `${label} must be an amount, like 12.50`);

/** How a product can be priced: its own rule (a mode), or (a product's, not a group's) a labour time (LabourTimeCard.tsx). */
export type PriceChoice = PriceMode | "labour";

export const MODES: { value: PriceChoice; label: string; hint: string }[] = [
    // ServiceM8's word for it (its Manual pricing strategy), as a Manual cost (SupplierPricesTab.tsx).
    { value: "manual", label: "Manual", hint: "The price stays the same when its cost changes." },
    { value: "percent", label: "Markup", hint: "A percentage on its cost (before tax): its price follows its cost." },
    { value: "labour", label: "Labour time", hint: "A set time on an hourly labour rate: its price and cost follow the rate's." },
];

export function PricingEditor(
    props: Readonly<{
        data: PriceListData;
        /** The rule as edited (Manual: its price). */
        rule: PricingRule;
        /** How it's taxed, as edited. */
        tax: TaxQuote;
        canEdit: boolean;
        /**
         * The price the draft gives, worked out, shown last with its tax picked inside it. Without one
         * (a group priced from each item's cost: each item's price is its own), a Tax dropdown is
         * there instead, saying `taxNote`.
         */
        price?: string;
        taxNote?: string;
        /** Why Markup can't be chosen right now (e.g. an item with no cost). */
        costBasedUnavailable?: string;
        /** An item's cost, before tax: its fixed price or markup says what it comes to on it (with a link to its Costing tab). A group's items each have their own: none. */
        costNet?: number;
        /** Its cost as estimated (once a cost to update is done: pricing.ts itemProfit), before tax: what its minimum markup is judged on. */
        estimatedCostNet?: number;
        onCostTab?: () => void;
        /** Said in place of the choice of how it's priced, which can't be made here (ServiceM8 prices it itself). */
        pricedBy?: string;
        /** A product (not a group): Labour time is offered, making it one (labourTime.ts); why it can't be, if it can't. */
        onLabour?: () => Promise<unknown>;
        labourUnavailable?: string;
        /** A draft is being edited: the editor is marked (its `footer` then its save bar). */
        editing: boolean;
        /** On a tinted panel (a product overriding its group's price): no green editing tint (over it, it reads muddy). */
        tinted?: boolean;
        footer?: ReactNode;
        boxRef?: RefObject<HTMLDivElement | null>;
        onRule: (patch: Partial<PricingRule>) => Promise<unknown>;
        onMode: (mode: PriceMode) => Promise<unknown>;
        onTax: (tax: TaxQuote) => Promise<unknown>;
    }>,
) {
    const { data, rule, tax } = props;
    const fmt = useMoney();
    const rate = rateFor(tax.rateUuid, data.taxRates);
    const basis: Basis = { includesTax: tax.includesTax, tax: rate ? rate.percent / 100 : 0 };
    const label = basisLabel(tax.includesTax, rate);
    const disabled = !props.canEdit;
    // Its cost (an item's), in the draft's terms, linking to its Costing tab: what a fixed price or markup is set against.
    const cost = props.costNet ?? 0;
    // A link in a hint to another of its tabs (its cost, what's in it).
    const tabLink = (text: string, title: string, open: (() => void) | undefined) =>
        open ? (
            <button type="button" className="cursor-pointer whitespace-nowrap text-brand-ink hover:underline" title={title} onClick={open}>
                {text}
            </button>
        ) : (
            <span className="whitespace-nowrap">{text}</span>
        );
    const costLink = props.costNet && props.onCostTab ? tabLink(`${fmt.money(fromNet(cost, basis.includesTax, basis.tax))} cost`, "Its cost (the Costing tab)", props.onCostTab) : null;
    const judgedOn = props.estimatedCostNet ?? props.costNet;
    const tooLittle = judgedOn ? minimumWarning(data.settings, props.price, basis, judgedOn, fmt) : undefined;

    // Its markup: a fixed price's is worked out from it on its cost (none to tell it from: "-"; a group's items each have their own).
    const fixedMarkup = rule.mode === "manual" ? markupOfBase(rule.basePrice, cost) : null;
    // A markup group's: its tier for the cost (the group's own window: each item by its own), not typed.
    const tiers = rule.mode === "percent" && rule.tiers?.length ? rule.tiers : null;
    const tier = tiers && props.costNet !== undefined ? tierFor(tiers, cost) : undefined;
    const markupNow = rule.mode === "percent" ? markupOf(rule, cost) : rule.markupValue;
    const markupEditable = (rule.mode === "percent" || fixedMarkup !== null) && !tiers;
    let markupShown = shownPercent(markupNow);
    if (rule.mode === "manual") markupShown = fixedMarkup === null ? "" : shownPercent(String(fixedMarkup));
    if (tiers && !tier) markupShown = "";
    let markupHint: ReactNode;
    if (rule.mode === "manual") {
        // Against its cost, as a markup says it: "+$38.05 inc GST on its $60.91 cost", or (red) under it.
        const over = fromNet(toNumber(rule.basePrice) - cost, basis.includesTax, basis.tax);
        if (costLink) {
            markupHint = (
                <>
                    <span className={clsx(over < 0 && "text-danger")}>
                        {over < 0 ? "-" : "+"}
                        {fmt.money(Math.abs(over))} {label}
                    </span>{" "}
                    on its {costLink}
                </>
            );
        } else markupHint = props.costNet === undefined ? "On each item's own cost." : "Needs a cost to work it out.";
    } else if (tiers && !tier) markupHint = "By each item's own cost.";
    else if (costLink) {
        // Typed or its group's tier: "+$13.20 inc GST on its $13.20 cost".
        markupHint = (
            <>
                +{fmt.money(fromNet((cost * toNumber(markupNow)) / 100, basis.includesTax, basis.tax))} {label} on its {costLink}
            </>
        );
    }

    // Typing the total: a fixed price is it; a markup's % is worked out on its cost to give it (no cost: it can't be typed).
    const totalLocked = rule.mode === "percent" && cost <= 0;
    const totalCheck = (v: string) => {
        const bad = amountCheck("Total price")(v);
        if (bad || rule.mode !== "percent") return bad;
        const pct = markupForTotal(v, basis, cost);
        return pct !== null && toNumber(pct) < 0 ? "Less than its cost: use Manual for that" : null;
    };
    const saveTotal = (v: string) => {
        if (rule.mode === "manual") return props.onRule({ basePrice: toStored(toNet(toNumber(v), basis.includesTax, basis.tax)) });
        const pct = rule.mode === "percent" ? markupForTotal(v, basis, cost) : null;
        // A total typed: a markup of its own, no longer its group's.
        return pct === null ? Promise.resolve() : props.onRule({ markupValue: pct });
    };
    const totalHint: ReactNode = totalLocked ? "Needs a cost to work a markup out from." : tooLittle;

    // A draft: saved by its window's bar, so its fields don't say they're saved.
    return (
        <DraftFields>
            <div ref={props.boxRef} className={clsx("space-y-4", props.editing && "-mx-4 -mt-3.5 -mb-4 rounded-md px-4 pt-3.5 pb-4", props.editing && !props.tinted && "bg-brand-500/5")}>
                {/* Its card's heading ("Pricing") says what it is; the chosen way's hint under it. */}
                {props.pricedBy ? (
                    <p className="m-0 text-sm text-muted">{props.pricedBy}</p>
                ) : (
                    <ChoiceField<PriceChoice>
                        label="Price is"
                        hideLabel
                        value={rule.mode}
                        disabled={disabled}
                        options={MODES.filter((m) => m.value !== "labour" || props.onLabour).map((m) => {
                            if (m.value === "percent") return { ...m, unavailable: props.costBasedUnavailable };
                            return m.value === "labour" ? { ...m, unavailable: props.labourUnavailable } : m;
                        })}
                        onSave={(choice) => (choice === "labour" ? (props.onLabour?.() ?? Promise.resolve()) : props.onMode(choice))}
                    />
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label="Markup"
                        value={markupShown}
                        placeholder="-"
                        suffix="%"
                        inputMode="decimal"
                        disabled={disabled || !markupEditable}
                        validate={amountCheck("Markup")}
                        live
                        hint={markupHint}
                        onSave={(pct) => props.onRule(rule.mode === "manual" ? { basePrice: baseForMarkup(pct, cost) } : { markupValue: pct })}
                    />
                    {props.price === undefined ? (
                        <TaxSelect data={data} tax={tax} disabled={disabled} hint={props.taxNote} onTax={props.onTax} />
                    ) : (
                        // The price it gives, with how it's taxed inside it. Typed: its markup (a fixed price: itself) is worked out to give it.
                        <QuotedAmountField
                            label="Total price"
                            amount={props.price}
                            placeholder="-"
                            quote={tax}
                            rates={data.taxRates}
                            disabled={disabled}
                            hint={totalHint}
                            amountLocked={totalLocked}
                            validate={totalCheck}
                            live
                            onSave={saveTotal}
                            onQuote={props.onTax}
                        />
                    )}
                </div>
                {props.footer}
            </div>
        </DraftFields>
    );
}

/** Under the total price: its markup on its cost (pricing.ts itemProfit) when it's under the minimum markup (Price List settings), as it's typed. Nothing otherwise. */
function minimumWarning(settings: PriceListSettings, price: string | undefined, basis: Basis, costNet: number, fmt: ReturnType<typeof useMoney>) {
    if (price === undefined || price === "") return undefined;
    const madeNet = toNumber(price) / (basis.includesTax ? 1 + basis.tax : 1) - costNet;
    const markupPct = costNet > 0 ? (madeNet / costNet) * 100 : null;
    if (!underMinimum(markupPct, settings.minMarkup) || markupPct === null) return undefined;
    // Short, so it fits on a line after the price the other way ("= $119.09 ex GST. -24% markup (min 20%)").
    let text = `${fmt.percent(Math.round(markupPct))} markup (min ${fmt.percent(settings.minMarkup)})`;
    if (settings.minMarkup <= 0) text = madeNet < 0 ? "Makes a loss" : "Makes nothing";
    return <span className="text-danger">{text}</span>;
}

/** How it's taxed, as a dropdown of its own (the same choices as the picker inside the money boxes). */
export function TaxSelect(props: Readonly<{ data: PriceListData; tax: TaxQuote; disabled: boolean; hint?: string; onTax: (tax: TaxQuote) => Promise<unknown> }>) {
    const options = quoteOptions(props.data.taxRates);
    if (!options.length) return null;
    return (
        <SelectField
            label="Tax"
            value={quoteKey(props.tax, props.data.taxRates)}
            disabled={props.disabled}
            options={options}
            hint={props.hint}
            onSave={(key) => {
                const picked = options.find((o) => o.value === key);
                return picked ? props.onTax(picked.quote) : Promise.resolve();
            }}
        />
    );
}
